<template>
  <form novalidate @submit.prevent="submitQuote">
    <LsSelect
      v-model="flower_bed_count"
      :options="flowerBedCountOptions"
      label="How many flower beds would you like to get cleaned up?"
      track-id="instant_quote_flower_bed_weeding_form-flower_bed_count"
      name="flower_bed_count"
      test-id="flower_bed_count"
      :validation="{ required: true }"
      @input="updateFlowerBedCount"
    />
    <div v-if="flower_beds.length">
      <LsInputLabel label="What is the size and location of the flower bed(s)?" />
      <div
        v-for="(bed, idx) in flower_beds"
        :key="idx"
        class="instant-quote-flower-bed-weeding-form--size-location-container"
        test-id="flower_bed"
      >
        <span class="instant-quote-flower-bed-weeding-form--size-location-count">
          #{{ idx + 1 }}
        </span>
        <LsSelect
          v-model="bed.size"
          :options="flowerBedSizeOptions"
          track-id="instant_quote_flower_bed_weeding_form-flower_bed_size"
          :name="`bed_size-${idx}`"
          test-id="bed_size"
          placeholder="- Size -"
          :validation="{ required: true }"
          class="instant-quote-flower-bed-weeding-form--size"
          @input="openBedSizeCustomModal(bed, idx)"
        />
        <LsSelect
          v-model="bed.location"
          :options="flowerBedLocationOptions"
          track-id="instant_quote_flower_bed_weeding_form-flower_bed_location"
          :name="`bed_location-${idx}`"
          test-id="bed_location"
          placeholder="- Location -"
          :validation="{ required: true }"
          class="instant-quote-flower-bed-weeding-form--location"
        />
      </div>
    </div>
    <LsRadios
      v-model="desired_handling"
      :validation="{ required: true }"
      label="Would you like your crew to cut the weeds with weed eater and then apply spray or gel?"
      :options="QuoteFlowerBedWeedingOptions.desiredHandling"
      name="desired_handling"
      track-id="instant_quote_flower_bed_weeding-desired_handling"
    />
    <LsRadios
      v-model="desired_haulaway"
      :validation="{ required: true }"
      label="Would you like your crew to haul and dispose of the weeds?"
      :options="QuoteFlowerBedWeedingOptions.desiredHaulawayIq"
      name="desired_haulaway"
      track-id="instant_quote_flower_bed_weeding-desired_haulaway"
    />
    <LsInput
      v-model="details"
      track-id="instant_quote_flower_bed_weeding_form-details"
      test-id="details"
      label="Any other details you would like your lawn care pro to know?"
      name="details"
      type="textarea"
    />
    <LsRadios
      v-model="cycle"
      :options="cycleOptions"
      name="cycle"
      :validation="{ required: true }"
      label="How frequently would you like to get flower bed weeding done?"
      track-id="instant_quote_flower_bed_weeding_form-cycle"
      :disabled="Boolean(instantQuote.currentQuote)"
    />
    <LsUploadPhotos
      v-model="images"
      track-id="instant_quote_flower_bed_weeding_form-images"
      test-id="images"
      label="Upload photos"
      name="images"
    />
    <div v-if="!instantQuote.currentQuote">
      <LsButton
        text="generate quote"
        track-id="instant_quote_leaf_removal_form-generate_quote"
        type="submit"
        :loading="instantQuote.loadingStatus.isUpdating"
      />
    </div>

    <InstantQuoteFormGeneratedQuote v-else :property_id="property_id" />
  </form>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import _ from 'lodash';
import { INSTANT_QUOTE__GENERATE_QUOTE, INSTANT_QUOTE__RESET_QUOTE } from '@/store/types';
import { lsModalService, lsRouterHelpers, lsValidationService } from 'ls-vue-common';
import { QuoteFlowerBedWeedingOptions, InstantQuoteConstants } from 'ls-constants';
import { lsInstantQuoteFormHelpers, lsUploadPhotosHelper } from '@/helpers';
import { BedSizeCustomModal } from '@/modals';
import {
  LsButton,
  LsInput,
  LsInputLabel,
  LsSelect,
  LsUploadPhotos,
  LsRadios,
} from '@/components/common';
import InstantQuoteFormGeneratedQuote from '../components/InstantQuoteFormGeneratedQuote.vue';

export default {
  name: 'InstantQuoteFlowerBedWeedingForm',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsButton,
    LsInput,
    LsInputLabel,
    LsSelect,
    LsUploadPhotos,
    LsRadios,
    InstantQuoteFormGeneratedQuote,
  },
  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      cycle: 28,
      flower_bed_count: null,
      customBedSizes: [],
      images: [],
      details: '',
      desired_haulaway: true,
      desired_handling: 'organic_materials',
      flower_beds: [],

      selectedBedIdx: null,
    };
  },
  computed: {
    ...mapState(['instantQuote']),
    cycleOptions() {
      return InstantQuoteConstants.cycleOptions;
    },
    QuoteFlowerBedWeedingOptions() {
      return _.cloneDeep(QuoteFlowerBedWeedingOptions);
    },
    flowerBedCountOptions() {
      return _.times(20, (idx) => {
        return { value: idx + 1, label: _.toString(idx + 1), key: idx + 1 };
      });
    },
    flowerBedSizeOptions() {
      const sizes = _.cloneDeep(QuoteFlowerBedWeedingOptions.sizes);
      const sizesAndCustomBeds = _.concat(this.customBedSizes, sizes);
      return _.map(sizesAndCustomBeds, (size) => {
        return { value: size.value, label: size.label, key: size.value };
      });
    },
    flowerBedLocationOptions() {
      const locations = _.cloneDeep(QuoteFlowerBedWeedingOptions.locations);
      return _.map(locations, (location) => {
        return {
          value: location.value,
          label: location.label,
          key: location.value,
        };
      });
    },
  },
  created() {
    this[INSTANT_QUOTE__RESET_QUOTE]();
  },
  methods: {
    ...mapActions([INSTANT_QUOTE__GENERATE_QUOTE, INSTANT_QUOTE__RESET_QUOTE]),
    close() {
      const idx = this.selectedBedIdx;

      if (_.get(this.flower_beds[idx], 'size') === 'custom') {
        this.flower_beds[idx] = { ...this.flower_beds[idx], size: null };
        this.updateFlowerBedCount(this.flower_bed_count);
      }
      this.selectedBedIdx = null;
    },
    setCustomBedSize({ length, width }) {
      const idx = this.selectedBedIdx;

      this.flower_beds[idx].size = `${length}ftx${width}ft`;
      this.customBedSizes = [
        ...this.customBedSizes,
        {
          value: `${length}ftx${width}ft`,
          label: `${length}ft x ${width}ft`,
          key: `${length}ftx${width}ft`,
        },
      ];
      this.selectedBedIdx = null;
    },
    openBedSizeCustomModal(bed, idx) {
      this.selectedBedIdx = idx;
      if (bed.size === 'custom') {
        lsModalService.open({
          parent: this,
          component: BedSizeCustomModal,
          props: {
            setCustomBedSize: this.setCustomBedSize,
          },
          events: {
            close: this.close,
          },
        });
      }
    },
    updateFlowerBedCount(count) {
      this.flower_beds = _.times(count, (idx) => {
        return _.get(this.flower_beds, idx, { size: null, location: null });
      });
    },

    async submitQuote() {
      const isFormValid = await this.$validator.validateAll();
      const isUploadValid = lsUploadPhotosHelper.isValidUpload(this.images);

      if (!isFormValid || !isUploadValid) {
        return lsValidationService.scrollToInvalid();
      }

      const servicePromoId = lsRouterHelpers.getParam(this.$route, 'service_promo_id');

      await this[INSTANT_QUOTE__GENERATE_QUOTE]({
        property_id: this.property_id,
        type: 'instant_quote_flower_bed_weeding',
        formData: {
          desired_haulaway: this.desired_haulaway,
          desired_handling: this.desired_handling,
          flower_beds: this.flower_beds,
          details: this.details,
          cycle: this.cycle,
          images: this.images,
          service_promo_id: servicePromoId ? _.toNumber(servicePromoId) : null,
        },
      });

      lsInstantQuoteFormHelpers.scrollToBottom({ document });
    },
  },
};
</script>

<style lang="scss" scoped>
form > :not(:last-child) {
  margin-bottom: 40px !important;
}
.instant-quote-flower-bed-weeding-form--size-location-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 10px;
}

.instant-quote-flower-bed-weeding-form--size {
  flex: 1;
  padding: 0px 10px;
}

.instant-quote-flower-bed-weeding-form--location {
  flex: 1;
}

.instant-quote-flower-bed-weeding-form--size-location-count {
  font-weight: bold;
  width: 5px;
  padding-right: 20px;
  padding-top: 5px;
}
</style>
